import {
  CHALLENGE_TYPE_OPTIONS,
  hasMeaningfulAnswerType,
  isKnownChallengeType,
} from '@/components/challenges/challengeKinds';
import type {
  AnswerType,
  ChallengeDifficulty,
  ChallengeType,
  Visibility,
} from '@/config/objects/challenges';
import { cn } from '@/lib/utils';
import {
  FormFieldGroup,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@startupquickstart/frontend';
import type { ChallengeEditorSectionProps } from '../types';
import { SectionCard } from './SectionCard';

const DIFFICULTY_OPTIONS: ReadonlyArray<{ value: ChallengeDifficulty; label: string }> = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

const VISIBILITY_OPTIONS: ReadonlyArray<{ value: Visibility; label: string }> = [
  { value: 'private', label: 'Private' },
  { value: 'unlisted', label: 'Unlisted' },
  { value: 'public', label: 'Public' },
];

const ANSWER_TYPE_OPTIONS: ReadonlyArray<{ value: AnswerType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'photo', label: 'Photo' },
  { value: 'video', label: 'Video' },
  { value: 'true_false', label: 'True / False' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'fill_in_the_blank', label: 'Fill in the Blank' },
  { value: 'puzzle', label: 'Puzzle' },
];

/** Name, type, difficulty, points, visibility (library) and answer type. */
export function DetailsSection({
  values,
  errors,
  updateField,
  context,
}: ChallengeEditorSectionProps) {
  // Legacy rows may carry a type that predates the unified enum (e.g.
  // 'trivia'). Surface it as a disabled option so the select still shows the
  // stored value instead of silently coercing it.
  const typeIsUnknown = !isKnownChallengeType(values.type);

  return (
    <SectionCard
      id="details"
      title="Challenge Details"
      description="Basic information about the challenge"
    >
      <FormFieldGroup name="name" label="Name" required error={errors.name}>
        <Input
          id="name"
          value={values.name}
          onChange={(e) => updateField('name', e.target.value)}
          placeholder="Enter challenge name"
          className={cn(errors.name && 'border-destructive')}
        />
      </FormFieldGroup>

      <div className="grid gap-6 md:grid-cols-2">
        <FormFieldGroup name="type" label="Type" required error={errors.type}>
          <Select
            value={values.type}
            onValueChange={(v) => updateField('type', v as ChallengeType)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {typeIsUnknown && (
                <SelectItem value={values.type} disabled>
                  {values.type} (legacy)
                </SelectItem>
              )}
              {CHALLENGE_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldGroup>

        <FormFieldGroup name="difficulty" label="Difficulty" required>
          <Select
            value={values.difficulty}
            onValueChange={(v) => updateField('difficulty', v as ChallengeDifficulty)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select difficulty" />
            </SelectTrigger>
            <SelectContent>
              {DIFFICULTY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldGroup>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormFieldGroup name="points" label="Points" required error={errors.points}>
          <Input
            id="points"
            type="number"
            min={0}
            value={values.points}
            onChange={(e) => updateField('points', parseInt(e.target.value, 10) || 0)}
            placeholder="Enter points value"
          />
        </FormFieldGroup>

        {context === 'library' && (
          <FormFieldGroup name="visibility" label="Visibility" required>
            <Select
              value={values.visibility}
              onValueChange={(v) => updateField('visibility', v as Visibility)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select visibility" />
              </SelectTrigger>
              <SelectContent>
                {VISIBILITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormFieldGroup>
        )}
      </div>

      {/* Roadblock + minigame types are driven entirely by `type` — an
          answer type is meaningless for them, so we hide it. */}
      {hasMeaningfulAnswerType(values.type) && (
        <FormFieldGroup
          name="answerType"
          label="Answer Type"
          description="What participants must submit to complete this challenge"
          required
        >
          <Select
            value={values.answerType}
            onValueChange={(v) => updateField('answerType', v as AnswerType)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select answer type" />
            </SelectTrigger>
            <SelectContent>
              {ANSWER_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldGroup>
      )}
    </SectionCard>
  );
}
